import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatPKR } from "@/lib/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Truck, FileText, RefreshCw, Package, Printer } from "lucide-react";

const COURIER_LABELS = { trax: "TRAX", postex: "PostEx" };
const COURIER_BADGE = {
  trax: "bg-sage/15 text-sage-dark border border-sage/40",
  postex: "bg-rose/15 text-rose-dark border border-rose/40",
};

export default function AdminLogistics() {
  const [orders, setOrders] = useState([]);
  const [awbMap, setAwbMap] = useState({}); // order_id → awb
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [courierChoice, setCourierChoice] = useState({}); // order_id → 'trax' | 'postex'

  const load = async () => {
    setLoading(true);
    try {
      const [oRes, aRes] = await Promise.all([
        api.get("/admin/orders"),
        api.get("/admin/awbs").catch(() => ({ data: [] })),
      ]);
      setOrders(oRes.data || []);
      const map = {};
      (aRes.data || []).forEach((a) => { map[a.order_id] = a; });
      setAwbMap(map);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const generateAwb = async (orderId, courier) => {
    setBusyId(orderId);
    try {
      const { data } = await api.post(`/admin/orders/${orderId}/awb`, { courier });
      setAwbMap((m) => ({ ...m, [orderId]: data }));
      toast.success(`AWB generated · ${data.awb_number}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AWB generation failed");
    } finally {
      setBusyId(null);
    }
  };

  // Show only orders that are ready to ship (Confirmed/Shipped/Pending) — not cancelled or delivered yet.
  const visibleOrders = orders.filter((o) => o.status !== "Cancelled");

  const stats = {
    total: visibleOrders.length,
    awb: Object.keys(awbMap).length,
    trax: Object.values(awbMap).filter((a) => a.courier === "trax").length,
    postex: Object.values(awbMap).filter((a) => a.courier === "postex").length,
  };

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="admin-logistics-page">
      <div className="flex justify-between items-end mb-8 gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow mb-1">Logistics & Billing</div>
          <h1 className="font-display text-4xl text-ink">AWB Labels</h1>
          <p className="text-ink-soft mt-2 max-w-xl text-sm">
            Generate Air Waybill (AWB) labels for TRAX or PostEx. Each order gets a unique tracking number you can print and stick on the parcel.
          </p>
        </div>
        <Button onClick={load} variant="outline" className="border-border-subtle text-ink-soft" data-testid="logistics-refresh">
          <RefreshCw size={14} className="mr-2" /> Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Stat icon={Package} label="Active Orders" value={stats.total} />
        <Stat icon={FileText} label="AWBs Generated" value={stats.awb} />
        <Stat icon={Truck} label="via TRAX" value={stats.trax} accent="sage" />
        <Stat icon={Truck} label="via PostEx" value={stats.postex} accent="rose" />
      </div>

      <div className="bg-white border border-border-subtle overflow-x-auto">
        <table className="w-full min-w-[900px]">
          <thead className="bg-cream-warm border-b border-border-subtle">
            <tr className="text-left text-[10px] uppercase tracking-widest text-ink-soft">
              <th className="p-4">Order</th>
              <th className="p-4">Customer</th>
              <th className="p-4">City</th>
              <th className="p-4">COD</th>
              <th className="p-4">AWB</th>
              <th className="p-4 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="p-8 text-center text-ink-soft">Loading…</td></tr>
            ) : visibleOrders.length === 0 ? (
              <tr><td colSpan={6} className="p-10 text-center">
                <div className="font-display text-2xl text-ink mb-1">No orders to ship</div>
                <div className="text-ink-soft text-sm">Once orders come in, you can generate AWB labels here.</div>
              </td></tr>
            ) : visibleOrders.map((o) => {
              const awb = awbMap[o.id];
              const courier = courierChoice[o.id] || "trax";
              return (
                <tr key={o.id} className="border-b border-border-subtle last:border-0 hover:bg-cream-warm/50" data-testid={`awb-row-${o.id}`}>
                  <td className="p-4">
                    <div className="font-semibold text-ink">{o.order_number}</div>
                    <div className="text-xs text-ink-soft">{new Date(o.created_at).toLocaleDateString("en-PK")}</div>
                  </td>
                  <td className="p-4">
                    <div className="text-ink">{o.customer.full_name}</div>
                    <div className="text-xs text-ink-soft">{o.customer.phone}</div>
                  </td>
                  <td className="p-4 text-ink-soft text-sm">{o.customer.city}</td>
                  <td className="p-4 text-ink font-semibold">{formatPKR(o.total)}</td>
                  <td className="p-4">
                    {awb ? (
                      <div className="flex flex-col gap-1">
                        <span className={`inline-block px-2 py-1 text-[10px] uppercase tracking-widest ${COURIER_BADGE[awb.courier]} w-fit`}>
                          {COURIER_LABELS[awb.courier]}
                        </span>
                        <span className="text-xs text-ink font-mono">{awb.awb_number}</span>
                      </div>
                    ) : (
                      <span className="text-xs text-ink-soft uppercase tracking-widest">Not generated</span>
                    )}
                  </td>
                  <td className="p-4 text-right">
                    <div className="flex justify-end gap-2">
                      {awb ? (
                        <Link
                          to={`/admin/orders/${o.id}/awb`}
                          className="inline-flex items-center gap-1 px-3 py-2 text-xs uppercase tracking-widest bg-sage hover:bg-sage-dark text-cream transition-colors"
                          data-testid={`view-awb-${o.id}`}
                        >
                          <Printer size={12} /> View Label
                        </Link>
                      ) : (
                        <>
                          <Select value={courier} onValueChange={(v) => setCourierChoice((c) => ({ ...c, [o.id]: v }))}>
                            <SelectTrigger className="w-28 h-9 text-xs" data-testid={`courier-select-${o.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="trax" data-testid={`courier-trax-${o.id}`}>TRAX</SelectItem>
                              <SelectItem value="postex" data-testid={`courier-postex-${o.id}`}>PostEx</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button
                            disabled={busyId === o.id}
                            onClick={() => generateAwb(o.id, courier)}
                            className="bg-sage hover:bg-sage-dark text-cream text-xs uppercase tracking-widest disabled:opacity-50"
                            data-testid={`generate-awb-${o.id}`}
                          >
                            Generate AWB
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent }) {
  const color = accent === "sage" ? "text-sage" : accent === "rose" ? "text-rose-dark" : "text-ink";
  return (
    <div className="bg-white border border-border-subtle p-5">
      <div className="flex items-start justify-between">
        <div className="label-eyebrow !text-[10px]">{label}</div>
        <Icon size={16} className={color} />
      </div>
      <div className="mt-3 font-display text-3xl text-ink">{value}</div>
    </div>
  );
}
